import { useEffect, useState } from "react";
import { ExternalLink, Images, Loader2, X } from "lucide-react";
import type { CustomMockup, ProductCategory } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../lib/http";

// Studio / Toplu Studio: urun fotografi <-> sahne eslemesi.
// Ustte urunun fotograflari: birine tiklayinca AKTIF olur. Altta konsept
// kutuphanesi ve ilham fotograflari (ilham ilanlarinin kendi fotograflari +
// urunun ilham analizleri): tiklanan, aktif urun fotografiyla uretilecek sahne
// olarak eklenir (ilham fotografi once konsept olarak kaydedilir). Baska urun
// fotografina gecip ayni sekilde devam edilir; ayni secime tekrar tiklamak cikarir.

type Group = { id: string; sku: string | null; title: string; url: string; images: string[] };
/** Eklenen sahne: hangi urun fotografiyla */
export type PickedScene = { conceptId: string; productRef: string };

const abs = (u?: string | null) => (!u ? "" : /^(https?:|data:|blob:)/.test(u) ? u : `${getBackendUrl()}${u}`);

export function InspirationConceptPicker({ cat, productTypeKey, library = [], picked, onAdded, onRemoved, onClose }: {
  cat: ProductCategory;
  productTypeKey?: string | null;
  /** Konsept kutuphanesi (urun tipine uyanlar) */
  library?: CustomMockup[];
  /** Su an secili sahneler (urun fotografi esliyle) */
  picked: PickedScene[];
  /** Sahne eklendi (yeni konsept ya da kutuphaneden) — productRef: aktif urun fotografi */
  onAdded: (m: CustomMockup, productRef: string) => void;
  onRemoved: (conceptId: string) => void;
  onClose: () => void;
}) {
  const photos = (cat.source?.images ?? []).slice(0, 12);
  const [active, setActive] = useState<string>(photos[0] ?? "");
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [all, setAll] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Ilham fotografindan acilan konsept: (urun fotografi + ilham url) -> konsept id
  const [created, setCreated] = useState<Record<string, string>>({});
  const key = (photo: string, url: string) => `${photo}|${url}`;

  useEffect(() => {
    setGroups(null); setErr(null);
    const q = new URLSearchParams();
    if (!all) {
      if (cat.source?.sku) q.set("sku", cat.source.sku);
      const ids = (cat.inspirations ?? []).map((i) => i.listingId).filter(Boolean);
      if (ids.length) q.set("listingIds", ids.join(","));
    }
    void fetch(`${getBackendUrl()}/api/analyses/inspiration-images?${q}`, { headers: authHeaders() })
      .then(async (r) => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error ?? `Yüklenemedi (${r.status})`); return j.data as Group[]; })
      .then((rows) => {
        // Urunun ilham ilanlarinin kayitli fotograflari (analizi olmasa da) — once onlar
        const own: Group[] = all ? [] : (cat.inspirations ?? [])
          .filter((i) => i.images?.length && !rows.some((g) => g.url.includes(i.listingId)))
          .map((i) => ({ id: `insp-${i.listingId}`, sku: null, title: i.title || `Liste ${i.listingId}`, url: i.url, images: i.images! }));
        setGroups([...own, ...rows]);
      })
      .catch((e) => { setErr(e instanceof Error ? e.message : String(e)); setGroups([]); });
  }, [cat, all]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pickedFor = (conceptId: string) => picked.find((p) => p.conceptId === conceptId);
  const photoNo = (u: string) => photos.indexOf(u) + 1;

  // Kutuphane konsepti: aktif urun fotografiyla ekle / ayni esle tekrar tiklaninca cikar
  function toggleLibrary(c: CustomMockup) {
    if (!active) return;
    const cur = pickedFor(c.id);
    if (cur && cur.productRef === active) { onRemoved(c.id); return; }
    onAdded(c, active);
  }

  async function toggleInspo(url: string, g: Group) {
    if (!active) return;
    const k = key(active, url);
    const existing = created[k];
    if (existing && pickedFor(existing)) { onRemoved(existing); return; }
    setBusy(url); setErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/from-url`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        // Etsy kucuk boyut yerine tam boyut (konsept kalitesi)
        body: JSON.stringify({ url: url.replace(/il_\d+xN/, "il_fullxfull"), kind: "concept", label: `İlham: ${g.title}`.slice(0, 120), productTypeKey: productTypeKey || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error ?? `Eklenemedi (${r.status})`);
      const m = j.data as CustomMockup;
      setCreated((s) => ({ ...s, [k]: m.id }));
      onAdded(m, active);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  }

  // Secili olan referans uzerinde hangi urun fotografina bagli oldugu (numara)
  const badge = (conceptId?: string) => {
    const p = conceptId ? pickedFor(conceptId) : undefined;
    if (!p) return null;
    const n = photoNo(p.productRef);
    return <span className={`absolute right-1 top-1 rounded-full px-1.5 text-[10px] font-bold leading-4 text-white ${p.productRef === active ? "bg-indigo-600" : "bg-gray-500"}`}>{n > 0 ? n : "✓"}</span>;
  };
  const tile = "relative aspect-square overflow-hidden rounded-lg border";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Sahne seç" className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Images className="h-4 w-4 text-indigo-600" />Ürün fotoğrafına sahne seç</h3>
            <p className="truncate text-xs text-gray-500">{cat.name}</p>
          </div>
          <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
        </div>

        {/* Urun fotograflari: aktif olan secilen sahnelerle uretilir */}
        <div className="border-b border-gray-100 px-5 py-3">
          <p className="mb-1.5 text-xs font-medium text-gray-700">1 · Ürün fotoğrafı seç <span className="font-normal text-gray-500">— sonra aşağıdan bu fotoğrafla üretilecek sahneleri seç</span></p>
          <div className="flex flex-wrap gap-2">
            {photos.map((u, i) => {
              const n = picked.filter((p) => p.productRef === u).length;
              return (
                <button key={u} type="button" onClick={() => setActive(u)} aria-pressed={active === u} title={`${i + 1}. ürün fotoğrafı`}
                  className={`relative h-20 w-20 overflow-hidden rounded-xl border-2 ${active === u ? "border-indigo-600 ring-2 ring-indigo-200" : "border-gray-200 opacity-75 hover:opacity-100"}`}>
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <span className="absolute left-1 top-1 rounded bg-white/90 px-1 text-[11px] font-bold text-gray-800">{i + 1}</span>
                  {n > 0 && <span className="absolute bottom-1 right-1 rounded-full bg-indigo-600 px-1.5 text-[11px] font-bold text-white">{n} sahne</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <p className="text-xs font-medium text-gray-700">2 · {active ? `${photoNo(active)}. fotoğraf için sahne seç` : "Önce ürün fotoğrafı seç"} <span className="font-normal text-gray-500">— rozet: hangi ürün fotoğrafına bağlı; tekrar tıklamak çıkarır</span></p>
          {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}

          {[
            ["Konsept kütüphanesi" + (productTypeKey ? " — bu tür" : ""), productTypeKey ? library.filter((c) => c.productTypeKey === productTypeKey) : library] as const,
            ...(productTypeKey ? [["Diğer konseptler (başka tür / türsüz)", library.filter((c) => c.productTypeKey !== productTypeKey)] as const] : []),
          ].filter(([, list]) => list.length > 0).map(([title, list]) => (
            <section key={title}>
              <p className="mb-1.5 text-sm font-medium text-gray-800">{title} <span className="font-normal text-gray-500">({list.length})</span></p>
              <div className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6 md:grid-cols-8">
                {list.map((c) => (
                  <button key={c.id} type="button" onClick={() => toggleLibrary(c)} disabled={!active} title={c.label}
                    className={`${tile} ${pickedFor(c.id) ? "border-indigo-400" : "border-gray-200 hover:border-indigo-400"}`}>
                    <img src={abs(c.thumbnailUrl ?? c.imageUrl)} alt="" loading="lazy" className="h-full w-full object-cover" />
                    {badge(c.id)}
                  </button>
                ))}
              </div>
            </section>
          ))}

          <p className="text-sm font-medium text-gray-800">İlham fotoğrafları</p>
          {groups === null && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" />Yükleniyor…</p>}
          {groups?.length === 0 && !err && (
            <div className="py-2">
              <p className="text-sm text-gray-600">{all ? "Fotoğraflı ilham analizi yok." : "Bu ürünün ilham fotoğrafı yok."}</p>
              {!all && <button onClick={() => setAll(true)} className="mt-1 text-sm font-medium text-indigo-700 hover:underline">Tüm ilham analizlerini göster</button>}
            </div>
          )}
          {groups?.map((g) => (
            <section key={g.id}>
              <div className="mb-1.5 flex items-center gap-2">
                <p className="min-w-0 flex-1 truncate text-sm text-gray-700" title={g.title}>{g.title}</p>
                {g.sku && <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{g.sku}</span>}
                <a href={g.url} target="_blank" rel="noreferrer" title="Etsy ilanını aç" aria-label="Etsy ilanını aç" className="shrink-0 rounded-lg p-1 text-gray-400 hover:text-indigo-700"><ExternalLink className="h-4 w-4" /></a>
              </div>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
                {g.images.map((u) => {
                  // Bu ilham fotografindan acilan sahnelerden biri (aktif foto once)
                  const cid = created[key(active, u)] ?? Object.entries(created).find(([k, id]) => k.endsWith(`|${u}`) && pickedFor(id))?.[1];
                  return (
                    <button key={u} type="button" onClick={() => void toggleInspo(u, g)} disabled={!active || !!busy}
                      title={active ? "Bu ürün fotoğrafının sahnesi olarak ekle / çıkar" : "Önce ürün fotoğrafı seç"}
                      className={`${tile} ${cid && pickedFor(cid) ? "border-indigo-400" : "border-gray-200 hover:border-indigo-400"} disabled:cursor-default`}>
                      <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
                      {busy === u && <span className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></span>}
                      {badge(cid)}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
          {groups && groups.length > 0 && !all && (
            <button onClick={() => setAll(true)} className="text-sm font-medium text-indigo-700 hover:underline">Tüm ilham analizlerini göster</button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <p className="text-sm text-gray-600">{picked.length} sahne seçili</p>
          <button onClick={onClose} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">Tamam</button>
        </div>
      </div>
    </div>
  );
}
